import type { Database, Tables } from './database.types.js';

// Vault and Scan helpers shared by the app, the MCP server and the Edge Functions.

export type VaultLink = Tables<'vault_links'>;
export type HiveFile = Tables<'files'>;
export type VaultLinkKind = Database['public']['Enums']['vault_link_kind'];

export const VAULT_KINDS: readonly { id: VaultLinkKind; name: string }[] = [
  { id: 'drive_doc', name: 'Google Doc' },
  { id: 'drive_sheet', name: 'Google Sheet' },
  { id: 'drive_slides', name: 'Google Slides' },
  { id: 'drive_folder', name: 'Drive folder' },
  { id: 'drive_video', name: 'Drive video' },
  { id: 'drive_file', name: 'Drive file' },
  { id: 'figma', name: 'Figma' },
  { id: 'web', name: 'Link' },
];

export const VAULT_KIND_IDS = VAULT_KINDS.map((k) => k.id) as [VaultLinkKind, ...VaultLinkKind[]];

export function vaultKindName(kind: VaultLinkKind): string {
  return VAULT_KINDS.find((k) => k.id === kind)?.name ?? kind;
}

export function isDriveKind(kind: VaultLinkKind): boolean {
  return kind.startsWith('drive_');
}

export interface ClassifiedUrl {
  /** The URL as it will be saved: trimmed, http(s) only, without tracking junk. */
  url: string;
  kind: VaultLinkKind;
  /** Drive file or folder id, or Figma file key. */
  providerId: string | null;
}

const DRIVE_ID = '([A-Za-z0-9_-]{10,})';

/**
 * Works out what a pasted link points at from its shape alone: Google Docs,
 * Sheets, Slides, Drive files and folders, Figma files, or anything else on
 * the web. Returns null for something that isn't an http(s) URL.
 */
export function classifyUrl(input: string): ClassifiedUrl | null {
  let u: URL;
  try {
    u = new URL(input.trim());
  } catch {
    return null;
  }
  if (u.protocol !== 'https:' && u.protocol !== 'http:') return null;
  for (const p of [...u.searchParams.keys()]) if (/^utm_/.test(p)) u.searchParams.delete(p);
  const url = u.toString();
  const host = u.hostname.toLowerCase();
  const path = u.pathname;

  if (host === 'docs.google.com') {
    const m = new RegExp(`^/(document|spreadsheets|presentation|forms)/(?:u/\\d+/)?d/${DRIVE_ID}`).exec(path);
    if (m) {
      const kind: VaultLinkKind =
        m[1] === 'document'
          ? 'drive_doc'
          : m[1] === 'spreadsheets'
            ? 'drive_sheet'
            : m[1] === 'presentation'
              ? 'drive_slides'
              : 'drive_file';
      return { url, kind, providerId: m[2]! };
    }
  }
  if (host === 'drive.google.com') {
    const folder = new RegExp(`/folders/${DRIVE_ID}`).exec(path);
    if (folder) return { url, kind: 'drive_folder', providerId: folder[1]! };
    const file = new RegExp(`/file/(?:u/\\d+/)?d/${DRIVE_ID}`).exec(path);
    if (file) return { url, kind: 'drive_file', providerId: file[1]! };
    const id = u.searchParams.get('id');
    if (id && new RegExp(`^${DRIVE_ID}$`).test(id)) return { url, kind: 'drive_file', providerId: id };
  }
  if (host === 'figma.com' || host === 'www.figma.com') {
    const m = /^\/(?:file|design|proto|board|slides)\/([A-Za-z0-9]{10,})/.exec(path);
    if (m) return { url, kind: 'figma', providerId: m[1]! };
  }
  return { url, kind: 'web', providerId: null };
}

/** The vault kind for a Drive file's MIME type (from Picker or the Drive API). */
export function driveKindFromMime(mimeType: string | null | undefined): VaultLinkKind {
  switch (mimeType) {
    case 'application/vnd.google-apps.document':
      return 'drive_doc';
    case 'application/vnd.google-apps.spreadsheet':
      return 'drive_sheet';
    case 'application/vnd.google-apps.presentation':
      return 'drive_slides';
    case 'application/vnd.google-apps.folder':
      return 'drive_folder';
    default:
      return mimeType?.startsWith('video/') ? 'drive_video' : 'drive_file';
  }
}

/** The narrow Drive scope: Hive sees only files it creates or that a member picks. */
export const DRIVE_FILE_SCOPE = 'https://www.googleapis.com/auth/drive.file';

/** Files at or above this size go to Google Drive, never Hive storage (Supabase's free tier has 1 GB). */
export const MAX_HIVE_UPLOAD_BYTES = 25 * 1024 * 1024;

/** Where an upload goes by default: video and anything 25 MB or more to Drive, the rest to Hive storage. */
export function uploadDestination(file: { type: string; size: number }): 'hive' | 'drive' {
  return file.type.startsWith('video/') || file.size >= MAX_HIVE_UPLOAD_BYTES ? 'drive' : 'hive';
}

/** A storage path for an upload: <game or studio>/<random>/<safe file name>. */
export function storagePathFor(gameSlug: string | null, fileName: string, random: string): string {
  const safe =
    fileName
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^A-Za-z0-9._-]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(-120) || 'file';
  return `${gameSlug ?? 'studio'}/${random}/${safe}`;
}

/** Things the Links panel and Scan can show. */
export type ItemType = 'game' | 'task' | 'idea' | 'doc' | 'vault_link' | 'file' | 'sprint' | 'message';

export const ITEM_TYPES: readonly { id: ItemType; name: string; plural: string }[] = [
  { id: 'task', name: 'Task', plural: 'Tasks' },
  { id: 'idea', name: 'Idea', plural: 'Ideas' },
  { id: 'doc', name: 'Doc', plural: 'Docs' },
  { id: 'vault_link', name: 'Link', plural: 'Links' },
  { id: 'file', name: 'File', plural: 'Files' },
  { id: 'sprint', name: 'Sprint', plural: 'Sprints' },
  { id: 'message', name: 'Message', plural: 'Messages' },
  { id: 'game', name: 'Game', plural: 'Games' },
];

export const ITEM_TYPE_IDS = ITEM_TYPES.map((t) => t.id) as [ItemType, ...ItemType[]];

export function itemTypeName(type: string): string {
  return ITEM_TYPES.find((t) => t.id === type)?.name ?? type;
}

/**
 * Where an item lives in the app. Tasks and sprints need their game's slug; docs open on
 * their idea (for a promoted idea's docs, the items view and search still give it).
 */
export function itemPath(
  item: { type: string; id: string; parent_id?: string | null },
  context: { gameSlug?: string | null } = {},
): string {
  switch (item.type) {
    case 'game':
      return `/grid/${context.gameSlug ?? 'studio'}`;
    case 'task':
      return `/grid/${context.gameSlug ?? 'studio'}?task=${item.id}`;
    case 'idea':
      return `/forge/ideas/${item.id}`;
    case 'doc':
      return item.parent_id ? `/forge/ideas/${item.parent_id}?doc=${item.id}` : '/forge';
    case 'message':
      // parent_id is the channel.
      return item.parent_id ? `/comms/${item.parent_id}?message=${item.id}` : '/comms';
    case 'sprint':
      return `/cycles/${context.gameSlug ?? 'studio'}?sprint=${item.id}`;
    case 'vault_link':
    case 'file':
      return `/vault?${item.type === 'file' ? 'file' : 'link'}=${item.id}`;
    default:
      return '/scan';
  }
}
